import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Webhook, RefreshCw, MousePointerClick } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const KIND_META = {
  webhook: { label: "Webhook", icon: Webhook, cls: "bg-violet-50 text-violet-700 border-violet-200" },
  catchup: { label: "Catch-up", icon: RefreshCw, cls: "bg-sky-50 text-sky-700 border-sky-200" },
  manual: { label: "Sync now", icon: MousePointerClick, cls: "bg-slate-100 text-slate-700 border-slate-200" },
};

function fmt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function countBits(c = {}) {
  const bits = [];
  if (c.orders_seen != null) bits.push(`${c.orders_seen} order${c.orders_seen === 1 ? "" : "s"} seen`);
  if (c.referrals) bits.push(`${c.referrals} referral${c.referrals === 1 ? "" : "s"}`);
  if (c.merged) bits.push(`${c.merged} merged into your logs`);
  if (c.touches) bits.push(`${c.touches} call/text touch${c.touches === 1 ? "" : "es"}`);
  if (c.enriched) bits.push(`${c.enriched} enriched`);
  if (c.realtors_created) bits.push(`${c.realtors_created} auto-created`);
  if (c.refunds) bits.push(`${c.refunds} refund${c.refunds === 1 ? "" : "s"} reconciled`);
  return bits;
}

/**
 * ISN update log — the last ~20 sync events (webhooks / catch-up / manual)
 * showing exactly what each run pulled and changed.
 */
export default function IsnUpdateLogDialog({ open, onClose, userId, targetName }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/isn/log", { params: userId ? { user_id: userId } : {} })
      .then(({ data }) => setEntries(data.entries || []))
      .catch((e) => toast.error("Couldn't load log", { description: formatApiError(e) }))
      .finally(() => setLoading(false));
  }, [open, userId]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="isn-update-log-dialog">
        <DialogHeader>
          <DialogTitle>ISN update log</DialogTitle>
          <DialogDescription>
            Recent sync events for {targetName}. Each entry shows what came in.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto -mx-1 px-1 space-y-2">
          {loading ? (
            <div className="py-8 text-center text-sm text-slate-500 flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : entries.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-400">
              No sync events yet. Hit <span className="font-medium">Sync now</span> to pull your latest ISN data.
            </div>
          ) : (
            entries.map((e) => {
              const meta = KIND_META[e.kind] || KIND_META.manual;
              const Icon = meta.icon;
              const ok = e.status === "ok";
              const bits = countBits(e.counts);
              return (
                <div key={e.id} className="rounded-md border border-slate-200 p-3" data-testid={`isn-log-entry-${e.id}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[11px] font-medium ${meta.cls}`}>
                      <Icon className="h-3 w-3" /> {meta.label}
                    </span>
                    <span className="text-[11px] text-slate-400">{fmt(e.created_at)}</span>
                  </div>
                  <div className={`mt-1.5 text-xs font-medium ${ok ? "text-slate-700" : "text-rose-600"}`}>
                    {ok ? (bits.length ? bits.join(" · ") : "No new activity") : `Error: ${e.error || "sync failed"}`}
                  </div>
                  {Array.isArray(e.details) && e.details.length ? (
                    <>
                      <ul className="mt-1.5 space-y-0.5 text-[11px] text-slate-500 list-disc list-inside">
                        {(expanded[e.id] ? e.details : e.details.slice(0, 12)).map((d, i) => (
                          <li key={i} className="break-words">{d}</li>
                        ))}
                      </ul>
                      {e.details.length > 12 ? (
                        <button
                          type="button"
                          onClick={() => setExpanded((m) => ({ ...m, [e.id]: !m[e.id] }))}
                          className="mt-1 text-[11px] font-medium text-sky-600 hover:text-sky-700"
                          data-testid={`isn-log-expand-${e.id}`}
                        >
                          {expanded[e.id] ? "Show less" : `+${e.details.length - 12} more…`}
                        </button>
                      ) : null}
                    </>
                  ) : null}
                </div>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
